// ============================================================================
// TRAINING REPORT PAGE - UPDATED WITH GLOSSARY TOOLTIPS
// ============================================================================
// Displays comprehensive training analysis with beginner-friendly tooltips
// ============================================================================

import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Spinner,
  Progress,
  Grid,
  GridItem,
  Divider,
  Badge,
  Collapse,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useBreakpointValue,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react';
import { 
  Trophy, 
  Target, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  CheckCircle,
  ChevronDown,
  ChevronUp,
  BookOpen,
  MessageCircle,
  Lightbulb,
  Award,
  BarChart2,
  Share2,
} from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import TermTooltip, { StatWithTooltip, GlossarySection, InlineTerm } from '../components/ui/TermTooltip';
import { getTrainingReport, type TrainingReport, POKER_GLOSSARY } from '../services/training';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// MOBILE UTILITIES
// ============================================================================

const triggerHaptic = (type: 'light' | 'medium' | 'success' = 'light') => {
  if ('vibrate' in navigator) {
    const patterns = { light: [10], medium: [25], success: [10, 50, 20] };
    navigator.vibrate(patterns[type]);
  }
};

const TOUCH_TARGETS = {
  minimum: '44px',
  comfortable: '48px',
  large: '56px',
};

// ============================================================================
// MINI CARD - renders "Ah" / "10d" style card strings as a small playing card
// ============================================================================

const SUIT_GLYPH: Record<string, string> = { h: '♥', d: '♦', c: '♣', s: '♠' };

const MiniCard: React.FC<{ card: string }> = ({ card }) => {
  const suitChar = card.slice(-1).toLowerCase();
  const rank = card.slice(0, -1).toUpperCase();
  const glyph = SUIT_GLYPH[suitChar] || '?';
  const isRed = suitChar === 'h' || suitChar === 'd';
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      bg="white"
      borderRadius="4px"
      px="5px"
      py="1px"
      minW="30px"
      border="1px solid #cbd5e0"
    >
      <Text fontSize="xs" fontWeight="800" color={isRed ? '#e53e3e' : '#1a202c'} lineHeight="1.3">
        {rank}{glyph}
      </Text>
    </Box>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

const TrainingReportPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [report, setReport] = useState<TrainingReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    hands: true,
    strengths: true,
    weaknesses: true,
    metrics: false,
    glossary: false,
    nextSteps: true,
  });

  useEffect(() => {
    if (reportId) {
      loadReport();
    }
  }, [reportId]);

  const loadReport = async () => {
    if (!reportId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Bounded: right after leaving a table the report write may still be
      // syncing - fail soft with a refresh hint instead of spinning forever.
      const reportData = await Promise.race([
        getTrainingReport(reportId),
        new Promise<null>(res => setTimeout(() => res(null), 8000)),
      ]);
      if (reportData) {
        setReport(reportData);
      } else {
        setError('Report is still syncing - give it a few seconds and refresh.');
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = useCallback((section: string) => {
    triggerHaptic('light');
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const getScoreColor = (score: number) => {
    if (score >= 8) return POKER_COLORS.darkGreen;
    if (score >= 6) return POKER_COLORS.gold;
    if (score >= 4) return 'orange.400';
    return POKER_COLORS.darkRed;
  };

  const getScoreLabel = (score: number) => {
    if (score >= 9) return 'Excellent';
    if (score >= 8) return 'Great';
    if (score >= 7) return 'Good';
    if (score >= 6) return 'Solid';
    if (score >= 5) return 'Average';
    if (score >= 4) return 'Needs Work';
    return 'Focus Here';
  };

  if (isLoading) {
    return (
      <Container maxW="4xl" py={8} pl={{ base: 4, md: 24 }}>
        <VStack spacing={4} py={20}>
          <Spinner size="xl" color={POKER_COLORS.cyan} thickness="4px" />
          <Text color="gray.400">Loading your training report...</Text>
        </VStack>
      </Container>
    );
  }

  if (error || !report) {
    return (
      <Container maxW="4xl" py={8} pl={{ base: 4, md: 24 }}>
        <PokerCard p={8}>
          <VStack spacing={4}>
            <Icon as={AlertCircle} boxSize={12} color={POKER_COLORS.darkRed} />
            <Text color={POKER_COLORS.darkRed} fontSize="lg">{error || 'Report not found'}</Text>
            <PokerButton variant="primary" onClick={() => navigate('/training')}>
              Back to Training
            </PokerButton>
          </VStack>
        </PokerCard>
      </Container>
    );
  }

  return (
    <Container 
      maxW="4xl" 
      py={{ base: 4, md: 6 }} 
      px={{ base: 3, md: 6 }}
      pl={{ base: 3, md: 24 }} 
      pb={{ base: 24, md: 8 }}
      css={{
        paddingTop: 'max(env(safe-area-inset-top), 16px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 96px)',
      }}
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box
          bgGradient={`linear(135deg, ${POKER_COLORS.darkTeal} 0%, #1a0a2e 100%)`}
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor={`${POKER_COLORS.cyan}33`}
        >
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <VStack align="start" spacing={1}>
              <HStack>
                <Icon as={Trophy} color={POKER_COLORS.gold} boxSize={6} />
                <Heading size="lg" color="white">Training Report</Heading>
              </HStack>
              <Text color="gray.400" fontSize="sm">
                {new Date(report.generatedAt).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
              <HStack spacing={4} mt={2}>
                <Badge bg={`${POKER_COLORS.cyan}22`} color={POKER_COLORS.cyan}>
                  {report.handsPlayed} {report.handsPlayed === 1 ? 'hand' : 'hands'}
                </Badge>
                <Badge bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold}>
                  {Number.isFinite((report as any).sessionDuration)
                    ? `${Math.max(1, Math.round((report as any).sessionDuration / 60000))} min`
                    : 'quick session'}
                </Badge>
                <Badge
                  bg={report.skillLevel?.toLowerCase() === 'beginner'
                    ? `${POKER_COLORS.darkGreen}22`
                    : report.skillLevel?.toLowerCase() === 'intermediate'
                      ? `${POKER_COLORS.gold}22`
                      : `${POKER_COLORS.cyan}22`
                  }
                  color={report.skillLevel?.toLowerCase() === 'beginner'
                    ? POKER_COLORS.darkGreen
                    : report.skillLevel?.toLowerCase() === 'intermediate'
                      ? POKER_COLORS.gold
                      : POKER_COLORS.cyan
                  }
                  textTransform="capitalize"
                >
                  {report.skillLevel}
                </Badge>
                {(report as any).challenge?.title && (
                  <Badge bg="rgba(245,158,11,0.15)" color="#f59e0b">
                    {(report as any).challenge.title}
                  </Badge>
                )}
              </HStack>
              {(report as any).challenge?.mission && (
                <Text color="gray.400" fontSize="xs" mt={1}>
                  Mission: {(report as any).challenge.mission}
                </Text>
              )}
            </VStack>
            
            {/* Overall Score */}
            <VStack spacing={0}>
              <Text
                fontSize="5xl"
                fontWeight="bold"
                color={getScoreColor(report.overallScore)}
                lineHeight="1"
              >
                {report.overallScore.toFixed(1)}
              </Text>
              <Text color="gray.400" fontSize="sm">out of 10</Text>
              <Badge
                mt={1}
                bg={`${getScoreColor(report.overallScore)}22`}
                color={getScoreColor(report.overallScore)}
              >
                {getScoreLabel(report.overallScore)}
              </Badge>
            </VStack>
          </HStack>
        </Box>

        {/* Quick Actions */}
        <HStack spacing={3} flexWrap="wrap">
          <PokerButton
            variant="primary"
            leftIcon={<MessageCircle size={18} />}
            onClick={() => navigate(`/training/coach?report=${reportId}`)}
          >
            Ask AI Coach About This Session
          </PokerButton>
          <PokerButton
            variant="secondary"
            leftIcon={<Target size={18} />}
            onClick={() => navigate('/training')}
          >
            New Training Session
          </PokerButton>
        </HStack>

        {/* Stats Overview with Tooltips */}
        <PokerCard>
          <HStack mb={4}>
            <Icon as={BarChart2} color={POKER_COLORS.cyan} />
            <Text color={POKER_COLORS.cyan} fontWeight="bold">Key Statistics</Text>
            <TermTooltip term="These numbers show how you played. Hover over each for an explanation." showIcon={false}>
              <Icon as={Lightbulb} color={POKER_COLORS.gold} boxSize={4} cursor="help" />
            </TermTooltip>
          </HStack>
          
          <Grid templateColumns="repeat(4, 1fr)" gap={4}>
            <StatWithTooltip
              label="VPIP"
              value={report.stats.vpip}
              term="vpip"
              valueColor={report.stats.vpip > 35 ? POKER_COLORS.darkRed : report.stats.vpip < 15 ? POKER_COLORS.gold : POKER_COLORS.darkGreen}
            />
            <StatWithTooltip
              label="PFR"
              value={report.stats.pfr}
              term="pfr"
              valueColor={report.stats.pfr > 30 ? POKER_COLORS.darkRed : report.stats.pfr < 10 ? POKER_COLORS.gold : POKER_COLORS.darkGreen}
            />
            <StatWithTooltip
              label="C-Bet"
              value={report.stats.cbetFrequency}
              term="cbet"
              valueColor={POKER_COLORS.cyan}
            />
            <VStack spacing={1} align="center">
              <TermTooltip term="aggFactor">
                <Text color="gray.400" fontSize="xs">Aggression</Text>
              </TermTooltip>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="xl">
                {report.stats.aggressionFactor.toFixed(1)}
              </Text>
            </VStack>
          </Grid>

          {/* VPIP/PFR Guide for Beginners */}
          {['beginner', 'developing'].includes(report.skillLevel?.toLowerCase?.() || '') && (
            <Box mt={4} p={3} bg={`${POKER_COLORS.gold}11`} borderRadius="md" borderLeft="3px solid" borderColor={POKER_COLORS.gold}>
              <Text color="gray.300" fontSize="sm">
                 <strong>What do these mean?</strong> Your <InlineTerm term="vpip" display="VPIP" /> of {report.stats.vpip.toFixed(0)}% means you voluntarily put money in on {report.stats.vpip.toFixed(0)}% of hands. 

                Ideal is 18-25%. Your <InlineTerm term="pfr" display="PFR" /> shows how often you raised preflop.
              </Text>
            </Box>
          )}
        </PokerCard>

        {/* 🃏 HAND-BY-HAND REVIEW - the exact hands from this session, with
            what you did, the coach's verdict, and the better line. This is
            where the learning happens: real hands, not abstractions. */}
        {report.keyHands && report.keyHands.length > 0 && (
          <PokerCard>
            <CollapsibleSection
              title="Hand-by-Hand Review"
              icon={Award}
              iconColor={POKER_COLORS.gold}
              isOpen={expandedSections.hands}
              onToggle={() => toggleSection('hands')}
              badge={report.keyHands.length}
            >
              <VStack spacing={3} align="stretch" pt={4}>
                <Text color="gray.400" fontSize="xs">
                  Every hand below is one you just played
                  {(report as any).challenge?.title
                    ? ` in ${(report as any).challenge.title}`
                    : ''}. Green means you made the right move, red means there
                  was a better one - read the note under each to see exactly
                  what and why{(report as any).challenge?.title
                    ? ', measured against how winners beat this challenge'
                    : ''}.
                </Text>
                {report.keyHands.map((h: any) => (
                  <Box
                    key={h.handNumber}
                    p={4}
                    bg={`${POKER_COLORS.darkTeal}66`}
                    borderRadius="lg"
                    borderLeft="3px solid"
                    borderColor={h.isMistake ? POKER_COLORS.darkRed : h.isGoodPlay ? POKER_COLORS.darkGreen : POKER_COLORS.gold}
                  >
                    <HStack justify="space-between" flexWrap="wrap" gap={2} mb={2}>
                      <HStack spacing={3}>
                        <Text color="gray.400" fontSize="xs" fontWeight="bold">
                          HAND #{h.handNumber}
                        </Text>
                        <HStack spacing={1}>
                          {(h.holeCards || []).map((c: string, i: number) => <MiniCard key={i} card={c} />)}
                        </HStack>
                        {h.board && h.board.length > 0 && (
                          <>
                            <Text color="gray.600" fontSize="xs">board</Text>
                            <HStack spacing={1}>
                              {h.board.map((c: string, i: number) => <MiniCard key={i} card={c} />)}
                            </HStack>
                          </>
                        )}
                      </HStack>
                      <Badge
                        bg={h.isMistake ? `${POKER_COLORS.darkRed}33` : h.isGoodPlay ? `${POKER_COLORS.darkGreen}33` : `${POKER_COLORS.gold}33`}
                        color={h.isMistake ? POKER_COLORS.darkRed : h.isGoodPlay ? POKER_COLORS.darkGreen : POKER_COLORS.gold}
                      >
                        {h.isMistake ? 'BETTER LINE AVAILABLE' : h.isGoodPlay ? 'WELL PLAYED' : 'REASONABLE'}
                      </Badge>
                    </HStack>

                    <HStack spacing={4} flexWrap="wrap" mb={2}>
                      <Text color="gray.400" fontSize="xs">
                        Position: <Text as="span" color="gray.200" fontWeight="600">{h.position || '-'}</Text>
                      </Text>
                      <Text color="gray.400" fontSize="xs" textTransform="capitalize">
                        Street: <Text as="span" color="gray.200" fontWeight="600">{h.street}</Text>
                      </Text>
                      {h.potSize > 0 && (
                        <Text color="gray.400" fontSize="xs">
                          Pot: <Text as="span" color="gray.200" fontWeight="600">{Number(h.potSize).toLocaleString()}</Text>
                        </Text>
                      )}
                      <Text color="gray.400" fontSize="xs" textTransform="capitalize">
                        You chose: <Text as="span" color={POKER_COLORS.cyan} fontWeight="600">{h.userAction}</Text>
                      </Text>
                    </HStack>

                    {h.explanation && (
                      <Text color="gray.300" fontSize="sm">{h.explanation}</Text>
                    )}
                    {h.conceptsTested && h.conceptsTested.length > 0 && (
                      <HStack spacing={2} mt={2} flexWrap="wrap">
                        {h.conceptsTested.map((c: string) => (
                          <Badge key={c} bg="whiteAlpha.200" color="gray.400" fontSize="9px">{c}</Badge>
                        ))}
                      </HStack>
                    )}
                  </Box>
                ))}
                <PokerButton
                  variant="secondary"
                  w="100%"
                  leftIcon={<MessageCircle size={16} />}
                  onClick={() => navigate(`/training/coach?report=${reportId}`)}
                >
                  Discuss These Hands with the AI Coach
                </PokerButton>
              </VStack>
            </CollapsibleSection>
          </PokerCard>
        )}

        {/* Metrics Breakdown */}
        <PokerCard>
          <CollapsibleSection
            title="Detailed Metrics"
            icon={BarChart2}
            isOpen={expandedSections.metrics}
            onToggle={() => toggleSection('metrics')}
          >
            <VStack spacing={4} align="stretch" pt={4}>
              {report.metrics.map((metric, index) => (
                <Box key={index}>
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <TermTooltip term={metric.name.toLowerCase().replace(/\s+/g, '_')}>
                        <Text color="gray.300" fontWeight="medium">{metric.name}</Text>
                      </TermTooltip>
                    </HStack>
                    <HStack spacing={2}>
                      <Text color={getScoreColor(metric.score)} fontWeight="bold">
                        {metric.score.toFixed(1)}/10
                      </Text>
                      <Badge
                        size="sm"
                        bg={`${getScoreColor(metric.score)}22`}
                        color={getScoreColor(metric.score)}
                      >
                        {getScoreLabel(metric.score)}
                      </Badge>
                    </HStack>
                  </HStack>
                  <Progress
                    value={metric.score * 10}
                    h="8px"
                    borderRadius="full"
                    bg={`${POKER_COLORS.darkTeal}66`}
                    sx={{
                      '& > div': {
                        background: getScoreColor(metric.score),
                      },
                    }}
                  />
                  {metric.feedback && (
                    <Text color="gray.500" fontSize="xs" mt={1}>
                      {metric.feedback}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          </CollapsibleSection>
        </PokerCard>

        {/* Strengths */}
        <PokerCard>
          <CollapsibleSection
            title="Your Strengths"
            icon={CheckCircle}
            iconColor={POKER_COLORS.darkGreen}
            isOpen={expandedSections.strengths}
            onToggle={() => toggleSection('strengths')}
            badge={report.topStrengths.length}
          >
            <VStack spacing={3} align="stretch" pt={4}>
              {report.topStrengths.length > 0 ? (
                report.topStrengths.map((strength, index) => (
                  <HStack
                    key={index}
                    p={3}
                    bg={`${POKER_COLORS.darkGreen}11`}
                    borderRadius="lg"
                    borderLeft="3px solid"
                    borderColor={POKER_COLORS.darkGreen}
                  >
                    <Icon as={CheckCircle} color={POKER_COLORS.darkGreen} boxSize={5} />
                    <Text color="gray.200" fontSize="sm">{strength}</Text>
                  </HStack>
                ))
              ) : (
                <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                  Play more hands to identify your strengths!
                </Text>
              )}
            </VStack>
          </CollapsibleSection>
        </PokerCard>

        {/* Weaknesses */}
        <PokerCard>
          <CollapsibleSection
            title="Areas to Improve"
            icon={AlertCircle}
            iconColor={POKER_COLORS.darkRed}
            isOpen={expandedSections.weaknesses}
            onToggle={() => toggleSection('weaknesses')}
            badge={report.topWeaknesses.length}
          >
            <VStack spacing={3} align="stretch" pt={4}>
              {report.topWeaknesses.length > 0 ? (
                report.topWeaknesses.map((weakness, index) => (
                  <HStack
                    key={index}
                    p={3}
                    bg={`${POKER_COLORS.darkRed}11`}
                    borderRadius="lg"
                    borderLeft="3px solid"
                    borderColor={POKER_COLORS.darkRed}
                  >
                    <Icon as={AlertCircle} color={POKER_COLORS.darkRed} boxSize={5} />
                    <Text color="gray.200" fontSize="sm">{weakness}</Text>
                  </HStack>
                ))
              ) : (
                <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                  Great job! No major weaknesses identified.
                </Text>
              )}
            </VStack>
          </CollapsibleSection>
        </PokerCard>

        {/* Next Steps / Recommended Drills */}
        {report.recommendedDrills && report.recommendedDrills.length > 0 && (
          <PokerCard>
            <CollapsibleSection
              title="Recommended Practice"
              icon={Target}
              iconColor={POKER_COLORS.gold}
              isOpen={expandedSections.nextSteps}
              onToggle={() => toggleSection('nextSteps')}
            >
              <VStack spacing={3} align="stretch" pt={4}>
                {report.recommendedDrills.map((drill, index) => (
                  <HStack
                    key={index}
                    p={3}
                    bg={`${POKER_COLORS.gold}11`}
                    borderRadius="lg"
                    justify="space-between"
                    cursor="pointer"
                    _hover={{ bg: `${POKER_COLORS.gold}22` }}
                    onClick={() => navigate(`/training/coach?drill=${encodeURIComponent(drill)}`)}
                  >
                    <HStack>
                      <Icon as={Target} color={POKER_COLORS.gold} boxSize={5} />
                      <Text color="gray.200" fontSize="sm">{drill}</Text>
                    </HStack>
                    <Icon as={ChevronDown} color="gray.500" transform="rotate(-90deg)" />
                  </HStack>
                ))}
                
                <PokerButton
                  variant="primary"
                  w="100%"
                  mt={2}
                  leftIcon={<MessageCircle size={18} />}
                  onClick={() => navigate(`/training/coach?report=${reportId}`)}
                >
                  Practice These with AI Coach
                </PokerButton>
              </VStack>
            </CollapsibleSection>
          </PokerCard>
        )}

        {/* Glossary Section */}
        {report.glossaryTerms && Object.keys(report.glossaryTerms).length > 0 && (
          <PokerCard>
            <CollapsibleSection
              title="Terms Used in This Report"
              icon={BookOpen}
              iconColor={POKER_COLORS.cyan}
              isOpen={expandedSections.glossary}
              onToggle={() => toggleSection('glossary')}
              badge={Object.keys(report.glossaryTerms).length}
            >
              <Box pt={4}>
                <GlossarySection 
                  terms={report.glossaryTerms} 
                  title="Click any term to learn more"
                />
              </Box>
            </CollapsibleSection>
          </PokerCard>
        )}

        {/* Detailed Examples (if available) */}
        {report.detailedExamples && report.detailedExamples.length > 0 && (
          <PokerCard>
            <HStack mb={4}>
              <Icon as={Lightbulb} color={POKER_COLORS.gold} />
              <Text color={POKER_COLORS.gold} fontWeight="bold">Hand Examples</Text>
            </HStack>
            <VStack spacing={4} align="stretch">
              {report.detailedExamples.map((example, index) => (
                <Box
                  key={index}
                  p={4}
                  bg={`${POKER_COLORS.darkTeal}66`}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={`${POKER_COLORS.cyan}22`}
                >
                  <HStack justify="space-between" mb={2}>
                    <Badge
                      bg={example.quality === 'good' 
                        ? `${POKER_COLORS.darkGreen}33`
                        : example.quality === 'mistake'
                          ? `${POKER_COLORS.darkRed}33`
                          : `${POKER_COLORS.gold}33`
                      }
                      color={example.quality === 'good'
                        ? POKER_COLORS.darkGreen
                        : example.quality === 'mistake'
                          ? POKER_COLORS.darkRed
                          : POKER_COLORS.gold
                      }
                    >
                      {example.quality === 'good' ? ' Good Play' : example.quality === 'mistake' ? ' Mistake' : '~ Learning Moment'}

                    </Badge>
                    <Text color="gray.500" fontSize="xs">Hand #{example.handNumber}</Text>
                  </HStack>
                  <Text color="gray.200" fontSize="sm" mb={2}>{example.situation}</Text>
                  <Text color="gray.400" fontSize="xs">{example.lesson}</Text>
                </Box>
              ))}
            </VStack>
          </PokerCard>
        )}

        {/* Back Button */}
        <HStack justify="center" pt={4}>
          <PokerButton variant="secondary" onClick={() => navigate('/training')}>
            Back to Training Lobby
          </PokerButton>
        </HStack>
      </VStack>
    </Container>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface CollapsibleSectionProps {
  title: string;
  icon: React.ElementType;
  iconColor?: string;
  isOpen: boolean;
  onToggle: () => void;
  badge?: number;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  iconColor = POKER_COLORS.cyan,
  isOpen,
  onToggle,
  badge,
  children,
}) => {
  return (
    <>
      <HStack
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ opacity: 0.8 }}
      >
        <HStack>
          <Icon as={icon} color={iconColor} boxSize={5} />
          <Text color={iconColor} fontWeight="bold">{title}</Text>
          {badge !== undefined && badge > 0 && (
            <Badge bg={`${iconColor}22`} color={iconColor} borderRadius="full">
              {badge}
            </Badge>
          )}
        </HStack>
        <Icon
          as={isOpen ? ChevronUp : ChevronDown}
          color="gray.500"
          boxSize={5}
        />
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        {children}
      </Collapse>
    </>
  );
};

// ============================================================================
// EXPORT
// ============================================================================

export default TrainingReportPage;